<template>
  <div class="top-page">
    <header class="top-header">
      <!-- 必須データが確定するまでは「Day —」（前の学習者の Day・仮の Day1 を出さない） -->
      <p class="day-label">{{ dayLabel({ requiredDataReady, currentDay }) }}</p>
    </header>

    <!-- 必須データ（会員状態・進捗）を取得できなかったとき。練習は始めさせず、読み直せるようにする -->
    <!-- 選んでいる子は利用できないが、利用できる兄弟がいる（指示書 B）。勝手に切り替えず、ボタンで選んでもらう -->
    <div v-if="requiredView === 'switch'" class="required-data-switch" role="alert">
      <p>{{ switchOffer.notice }}</p>
      <div class="required-data-switch-buttons">
        <button
          v-for="c in switchOffer.candidates"
          :key="c.learner_id"
          type="button"
          class="btn btn-secondary"
          @click="switchToLearner(c.learner_id)"
        >
          {{ c.name ? `${c.name}さんに切り替える` : 'このお子さまに切り替える' }}
        </button>
      </div>
    </div>

    <div v-if="requiredView === 'error'" class="required-data-error" role="alert">
      <p>{{ requiredDataError }}</p>
      <button type="button" class="btn btn-secondary" @click="retryRequiredData">もう一度読み込む</button>
    </div>

    <main class="top-main">
      <div class="buttons">
        <div
          class="feedback-btn-wrap"
          :class="{ 'feedback-btn-wrap--badged': latestFeedbackDay != null }"
        >
          <span
            v-if="latestFeedbackDay != null"
            class="feedback-day-badge"
            aria-hidden="true"
          >Day {{ latestFeedbackDay }}</span>
          <button
            type="button"
            class="btn btn-primary"
            :class="{ 'btn-primary--playing': feedbackPlaying }"
            :disabled="feedbackLoading || loading || feedbackPlaying"
            @click="playLatestFeedback"
          >
            <span v-if="feedbackLoading" class="loading-content">読み込み中...</span>
            <span v-else-if="feedbackPlaying" class="playing-content">
              <span class="icon-playing">🔊</span> 再生中...
            </span>
            <span v-else>▶️ 先生のコメントを聞く</span>
          </button>
        </div>
        <p v-if="feedbackMessage" class="feedback-message">{{ feedbackMessage }}</p>
        <div v-show="feedbackVideoVisible" class="feedback-video-overlay" @click.self="closeFeedbackVideo">
          <video
            ref="feedbackVideoEl"
            class="feedback-video-player"
            playsinline
            controls
            @ended="onFeedbackVideoEnded"
            @error="onFeedbackVideoError"
          >
            お使いのブラウザは動画再生に対応していません。
          </video>
          <img
            v-if="assignmentImageUrl && !assignmentImageLoadFailed"
            :src="assignmentImageUrl"
            class="feedback-assignment-image"
            alt="この日の課題内容"
            @error="onAssignmentImageError"
          />
          <button type="button" class="feedback-video-close" @click="closeFeedbackVideo">✕ 閉じる</button>
        </div>
        <!-- 必須データの読み込み中（初回・学習者の切り替え・再試行）。コメント情報の後読みでは出さない -->
        <div v-if="requiredView === 'loading'" class="required-data-loading" role="status" aria-live="polite">
          <span class="required-data-spinner" aria-hidden="true"></span>
          <span>練習の準備をしています…</span>
        </div>
        <button
          type="button"
          class="btn btn-secondary"
          :disabled="!canStartPractice({ loading, requiredDataReady })"
          @click="goToPractice"
        >
          今日の練習をはじめる
        </button>
        <button
          type="button"
          class="btn btn-mic-check"
          :disabled="loading"
          @click="showMicCheckModal = true"
        >
          🎤 マイク設定確認
        </button>
        <p class="mic-check-hint">
          初回利用時はこのボタンを押して、マイクが利用できるようになっているかご確認ください。利用できない方は、設定を変更ください。
        </p>
        <!-- これまでのレッスン動画（見るだけ）。今日の練習が主役のまま、Day2 から小さなリンクで出す -->
        <router-link
          v-if="showPastLessonVideosLink({ requiredDataReady, currentDay })"
          to="/past-videos"
          class="past-videos-link"
        >
          これまでのレッスン動画を見る ›
        </router-link>
      </div>
    </main>

    <!-- 初期設定完了 歓迎モーダル -->
    <div v-if="showWelcomeModal" class="modal-overlay" @click.self="closeWelcomeModal">
      <div class="modal-box">
        <p class="modal-emoji">🎉</p>
        <h2 class="modal-title">設定が完了しました！</h2>
        <p class="modal-body">
          それでは、最初のDay1の練習にチャレンジしてみましょう！
        </p>
        <p class="modal-body modal-body--sub">
          お子さまの端末で学習する場合は、保護者の登録したメールアドレス・パスワードでログインすると、同様にご利用いただけます。
        </p>
        <button type="button" class="btn btn-primary" @click="closeWelcomeModal">
          はじめる
        </button>
      </div>
    </div>

    <!-- マイク設定確認モーダル -->
    <div v-if="showMicCheckModal" class="modal-overlay" @click.self="showMicCheckModal = false">
      <div class="modal-box modal-box--wide">
        <h2 class="modal-title">マイク設定確認</h2>
        <MicCheckPanel />
        <button type="button" class="btn btn-secondary modal-close-btn" @click="showMicCheckModal = false">
          閉じる
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, onUnmounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { supabase } from '../supabaseClient'
import { getApiBase } from '../utils/apiBase.js'
import { markLoginStage } from '../utils/loginTelemetry.js'
import { canStartPractice, loadCurrentDay, requiredDataView, dayLabel } from '../utils/topRequiredData.js'
import { track, generatePlaybackId, getSessionId } from '../utils/feedbackTracker.js'
import { useSelectedLearner, abandonLearnersInFlight } from '../composables/useSelectedLearner.js'
import { getAuthUserShared, abandonAuthCheckInFlight } from '../composables/useAuthUser.js'
import { getFamilyMembership, abandonFamilyMembershipInFlight } from '../composables/useFamilyMembership.js'
import { decideLearnerEntry, switchNoticeText } from '../utils/familyMembership.js'
import { createLoadDeadline, isLoadDeadlineError, TOP_REQUIRED_LOAD_TIMEOUT_MS } from '../utils/loadDeadline.js'
import { classifyGetUserResult } from '../utils/guardAccess.js'
import { createLatestOnly } from '../utils/latestOnly.js'
import MicCheckPanel from '../components/MicCheckPanel.vue'
import { showPastLessonVideosLink } from '../utils/pastLessonVideos.js'

const router = useRouter()
const route = useRoute()
const loading = ref(true)
const currentDay = ref(1)
// 必須データ（会員状態・進捗）が確定したか。失敗時は false のまま（仮のDayで練習を始めさせない）
const requiredDataReady = ref(false)
const requiredDataError = ref('')
// 練習エリアの表示（読み込み中・失敗・確定）
const requiredView = computed(() => requiredDataView({
  loading: loading.value,
  requiredDataReady: requiredDataReady.value,
  requiredDataError: requiredDataError.value,
  switchOffer: switchOffer.value
}))
const REQUIRED_DATA_ERROR_MESSAGE =
  '読み込みに失敗しました。通信状態を確認して、「もう一度読み込む」を押してください。'
// 練習開始に必要な読み込み全体が期限（TOP_REQUIRED_LOAD_TIMEOUT_MS）を過ぎたとき
const REQUIRED_DATA_TIMEOUT_MESSAGE =
  '読み込みに時間がかかっています。通信状態を確認して、もう一度お試しください。'
let currentUserId = null

// 初期設定完了直後の歓迎モーダル（Onboarding.vue から /top?onboarded=1 で遷移してきたときのみ表示）
const showWelcomeModal = ref(route.query.onboarded === '1')
const showMicCheckModal = ref(false)
function closeWelcomeModal() {
  showWelcomeModal.value = false
}
if (showWelcomeModal.value) {
  // 一度表示したらURLからクエリを除去（リロード時等に再表示しないように）
  router.replace({ path: route.path, query: {} })
}

const feedbackLoading = ref(false)
const feedbackPlaying = ref(false)
const feedbackMessage = ref(null)
// 動画フィードバック用（feedback_mime_type が video/ の場合のみ使用）
const feedbackVideoVisible = ref(false)
const feedbackVideoEl = ref(null)
/** 動画フィードバックと一緒に表示する、その日の課題内容の画像（横向き・852x480想定） */
const assignmentImageUrl = ref(null)
const assignmentImageLoadFailed = ref(false)
const ASSIGNMENT_IMAGE_BASE_URL = 'https://audio.otoeigo.com/phase2/assignment-images'
function assignmentImageUrlForDay(day) {
  if (day == null) return null
  return `${ASSIGNMENT_IMAGE_BASE_URL}/day${day}.png`
}
/** 直近フィードバックの提出に紐づく Day（バッジ表示用）／種別（トラッキング用） */
const latestFeedbackDay = ref(null)
const latestFeedbackType = ref(null)
const RESYNC_INTERVAL_MS = 5 * 60 * 1000
const lastResyncAt = ref(0)
const lastResyncYmd = ref('')
let isResyncing = false

const { selectedLearnerId, loadLearners, setSelectedLearner } = useSelectedLearner()

// 読み込みの世代。学習者の切り替え・再読み込みで、前の読み込みの遅い応答が新しい表示を上書きしないようにする（指示書 A-17）
const progressLoads = createLatestOnly()
const feedbackMetaLoads = createLatestOnly()

// 最新コメントの Day バッジ（任意の情報。指示書 ⑫）。練習開始の準備を待たせないよう、必須の読み込みの後で、待たずに取得する。
// 失敗しても練習は始められる。学習者を切り替えた後に、前の学習者の応答でバッジを書き換えない
// 実行中のコメント情報の取得を無効にして、表示を消す（学習者の切り替え・再読み込みの時点で呼ぶ）
const resetLatestFeedbackMeta = () => {
  feedbackMetaLoads.begin()
  latestFeedbackDay.value = null
  latestFeedbackType.value = null
}

const loadLatestFeedbackMeta = async () => {
  resetLatestFeedbackMeta()
  const isCurrent = feedbackMetaLoads.begin()
  if (!currentUserId) return
  try {
    let query = supabase
      .from('submissions_v3')
      .select('day')
      .eq('user_id', currentUserId)
      .not('feedback_file_key', 'is', null)
      .order('feedback_submitted_at', { ascending: false, nullsFirst: false })
      .limit(1)
    if (selectedLearnerId.value) {
      query = query.eq('learner_id', selectedLearnerId.value)
    }
    const { data: latestList, error: listError } = await query
    if (!isCurrent()) return
    if (listError || !latestList?.length) return
    latestFeedbackDay.value = latestList[0].day
  } catch (e) {
    console.warn('直近FBメタ取得エラー:', e)
  }
}

// 利用可否の確認（子ども単位。家族API /api/family-membership。判定はサーバーの会員状態APIと共通。指示書 B）
// 戻り値: { status: 'allowed' } 利用可 /
//         { status: 'switch', selected, candidates } 選んでいる子は利用できないが、利用できる兄弟がいる（切り替えのボタンを出す）/
//         { status: 'denied', message } 誰も利用できない（マイページへ案内）/
//         { status: 'error' } 取得できなかった（利用不可と断定しない）
// ここでは案内・画面遷移をしない。学習者の切り替え後に届いた前の学習者の結果で、マイページへ移さないため（呼び出し側で最新か確かめる）
const DENIED_DEFAULT_MESSAGE =
  '現在のプランではこの学習画面をご利用いただけません。\nマイページからサブスクリプション状況をご確認ください。'
const checkSubscriptionAccess = async (learnersStatus, lid, { force = false } = {}) => {
  if (!lid) {
    // 学習者一覧の取得そのものが失敗した場合は、0件（未登録）と扱わない（指示書 A-10）
    if (learnersStatus === 'error') return { status: 'error' }
    return { status: 'denied', message: '学習者が登録されていないか、選択できません。\nマイページからご確認ください。' }
  }

  let result = await getFamilyMembership({ force })
  if (result.kind === 'no_session') return { status: 'denied', message: 'ログインが必要です。' }
  if (result.kind !== 'ok') {
    console.warn('会員状態（家族）取得エラー')
    return { status: 'error' }
  }
  let entry = decideLearnerEntry({ family: result.family, selectedLearnerId: lid })
  // 使い回した結果（兄弟のだれかが利用可のとき10秒）で「この子は利用できない」と決めない。
  // その子の契約の再開・体験の開始の直後かもしれないので、取り直してから決める
  if (entry.kind !== 'allowed' && result.fromCache) {
    result = await getFamilyMembership({ force: true })
    if (result.kind === 'no_session') return { status: 'denied', message: 'ログインが必要です。' }
    if (result.kind !== 'ok') return { status: 'error' }
    entry = decideLearnerEntry({ family: result.family, selectedLearnerId: lid })
  }
  if (entry.kind === 'allowed') return { status: 'allowed' }
  if (entry.kind === 'switch') return { status: 'switch', selected: entry.selected, candidates: entry.candidates }
  if (entry.kind === 'denied_all') {
    return { status: 'denied', message: (entry.selected.message && String(entry.selected.message).trim()) || DENIED_DEFAULT_MESSAGE }
  }
  // 選んでいる子が家族の一覧に無い（学習者の追加・削除の直後など）。未契約とは扱わず、読み直してもらう
  return { status: 'error' }
}

// 選んでいる子が利用できず、利用できる兄弟がいるときの案内（勝手に切り替えない。ボタンで選んでもらう）
const switchOffer = ref(null)
const switchToLearner = (learnerId) => {
  setSelectedLearner(learnerId)
}

// 練習開始に必要な読み込み（認証・学習者一覧・利用可否・進捗）の、実行中の期限。新しい読み込みで前のものを中止する
let currentRequiredLoad = null

// 現在のDayは RPC で取得（兄弟対応: 選択中の子の進度を使用）。
// 全体に待機上限（開始から合計 TOP_REQUIRED_LOAD_TIMEOUT_MS）を設ける（指示書 A5）。コメント情報は対象外
const loadProgress = async (opts = {}) => {
  const isCurrent = progressLoads.begin()
  currentRequiredLoad?.cancel()
  const deadline = createLoadDeadline({ timeoutMs: TOP_REQUIRED_LOAD_TIMEOUT_MS })
  currentRequiredLoad = deadline
  // 前の学習者のコメント情報は、この時点で無効にして消す（新しい進捗の取得を待たない）
  resetLatestFeedbackMeta()
  requiredDataReady.value = false
  requiredDataError.value = ''
  switchOffer.value = null
  try {
    await runRequiredLoad(isCurrent, deadline, opts)
  } catch (e) {
    if (!isLoadDeadlineError(e)) throw e
    // 期限切れ。後から届く応答は、最新の読み込みではないので使わない
    if (!isCurrent()) return
    progressLoads.begin()
    // 止まった共有の要求を、再試行で待ち直さない（認証の確認・学習者一覧は、次は新しく取得する）
    abandonAuthCheckInFlight()
    abandonLearnersInFlight()
    abandonFamilyMembershipInFlight()
    // 練習は始めさせない（仮の Day1 にしない）。ログアウト・未契約への振り分けもしない
    loading.value = false
    requiredDataReady.value = false
    requiredDataError.value = REQUIRED_DATA_TIMEOUT_MESSAGE
    markLoginStage('top_required_data_failed', { reason: 'timeout' })
  } finally {
    deadline.finish()
  }
}

const runRequiredLoad = async (isCurrent, deadline, { force = false } = {}) => {
  // 認証の確認は、直前のガードの結果を共有する（指示書 ④⑩）
  let userResult
  try {
    userResult = await deadline.race(getAuthUserShared())
  } catch (e) {
    if (isLoadDeadlineError(e)) throw e
    userResult = { data: { user: null }, error: e || new Error('get_user_failed') }
  }
  if (!isCurrent()) return
  const userKind = classifyGetUserResult(userResult)
  if (userKind === 'error') {
    // 通信の失敗は、未ログインと扱わない
    loading.value = false
    requiredDataError.value = REQUIRED_DATA_ERROR_MESSAGE
    markLoginStage('top_required_data_failed', { reason: 'get_user_error' })
    return
  }
  const user = userKind === 'user' ? userResult.data.user : null
  if (!user) {
    router.push('/')
    return
  }
  // 兄弟対応: 先に学習者一覧を復元しないと selectedLearnerId が null のままになり、
  // learner-membership-status に learner_id を渡せない
  const learnersStatus = await deadline.race(loadLearners())
  if (!isCurrent()) return
  const lid = selectedLearnerId.value
  const access = await deadline.race(checkSubscriptionAccess(learnersStatus, lid, { force }))
  // 学習者の切り替え後に届いた、前の学習者の契約の結果では、案内も画面遷移もしない
  if (!isCurrent()) return
  if (lid !== selectedLearnerId.value) {
    // 確認中に学習者が切り替わった（初回の読み込み中など、切り替えの再読み込みがまだ始まっていない）。新しい学習者で読み直す
    loadProgress()
    return
  }
  if (access.status === 'switch') {
    // 選んでいる子は利用できないが、利用できる兄弟がいる。マイページへ移さず、切り替えのボタンを出す（練習は無効のまま）
    loading.value = false
    switchOffer.value = { notice: switchNoticeText(access.selected), candidates: access.candidates }
    markLoginStage('top_required_data_failed', { reason: 'selected_learner_not_active' })
    return
  }
  if (access.status !== 'allowed') {
    loading.value = false
    if (access.status === 'error') requiredDataError.value = REQUIRED_DATA_ERROR_MESSAGE
    // 観測（指示書A7）。ログイン試行中だけ記録される
    markLoginStage('top_required_data_failed', { reason: access.status === 'error' ? 'membership_check_error' : 'membership_not_ok' })
    if (access.status === 'denied') {
      alert(access.message)
      router.push('/mypage')
    }
    return
  }
  currentUserId = user.id
  let progressFailed = false
  let day = null
  try {
    // 進級チェックのRPC → だめなら保存済みの current_day。どちらも取れなければ失敗（仮の Day1 にしない）
    day = await deadline.race(lid
      ? loadCurrentDay({
        callRpc: () => supabase.rpc('advance_current_day_if_eligible_for_learner', { p_learner_id: lid }).abortSignal(deadline.signal),
        readStoredDay: () => supabase.from('learners').select('current_day').eq('id', lid).abortSignal(deadline.signal).maybeSingle()
      })
      : loadCurrentDay({
        callRpc: () => supabase.rpc('advance_current_day_if_eligible').abortSignal(deadline.signal),
        readStoredDay: () => supabase.from('users_phase2').select('current_day').eq('id', user.id).abortSignal(deadline.signal).maybeSingle()
      }))
  } catch (err) {
    if (isLoadDeadlineError(err)) throw err
    console.error('進捗取得エラー:', err)
    // 仮の Day1 で練習を始めさせない（必須データ未確定として扱う）
    progressFailed = true
  }
  // 学習者を切り替えた後などに届いた、前の読み込みの結果は使わない
  if (!isCurrent()) return
  loading.value = false
  if (progressFailed) {
    requiredDataError.value = REQUIRED_DATA_ERROR_MESSAGE
  } else {
    currentDay.value = day
    requiredDataReady.value = true
  }
  markLoginStage(progressFailed ? 'top_required_data_failed' : 'top_required_data_ready', progressFailed ? { reason: 'progress_error' } : undefined)
  // コメントの Day バッジは、練習開始の準備の後で取得する（待たない。指示書 ⑫・A-12）
  void loadLatestFeedbackMeta()
}

// 必須データの読み直し（パスワードの再入力は不要）
const retryRequiredData = () => {
  loading.value = true
  // 再試行では、会員状態を取り直す（前の結果を使わない）
  loadProgress({ force: true })
}

// 兄弟切り替え時に進捗を再読み込み。
// 最初の読み込みが失敗・期限切れで終わった後の切り替えでも読み込む（前の学習者のエラーのまま残さない）。
// 最初の読み込みの中で学習者一覧が選択を決める（未選択 → 1人目）ときは、その読み込みが続けて使うので読み直さない
watch(selectedLearnerId, (newId, oldId) => {
  resetLatestFeedbackMeta()
  if (currentUserId || oldId != null) {
    loading.value = true
    loadProgress()
  }
})

// iOS ではユーザー操作直後に play() を呼ばないと再生がブロックされるため、
// タップ直後に無音を再生して「アンロック」してから本番 URL を再生する
const SILENT_WAV_DATA_URL = 'data:audio/wav;base64,UklNRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA='

// 動画フィードバックのトラッキング情報（再生終了/エラー時に使用）
let feedbackVideoTrackInfo = null

function resetAssignmentImage() {
  assignmentImageUrl.value = null
  assignmentImageLoadFailed.value = false
}

function onAssignmentImageError() {
  // その日の画像がまだ用意されていない（180枚を順次追加していく運用のため）場合は
  // 静かに非表示にするだけにする。フィードバック動画自体の再生には影響させない。
  assignmentImageLoadFailed.value = true
}

function closeFeedbackVideo() {
  const videoEl = feedbackVideoEl.value
  if (videoEl) {
    videoEl.pause()
    videoEl.removeAttribute('src')
    videoEl.load()
  }
  feedbackVideoVisible.value = false
  feedbackPlaying.value = false
  feedbackVideoTrackInfo = null
  resetAssignmentImage()
}

function onFeedbackVideoEnded() {
  // 再生終了してもモーダルは自動で閉じない（✕閉じるボタンで自力で閉じる設計）。
  // 課題画像を見ながらもう一度動画を見返せるよう、video要素・状態はそのまま残す。
  feedbackPlaying.value = false
  const videoEl = feedbackVideoEl.value
  if (feedbackVideoTrackInfo) {
    const positionMs = Math.round((videoEl?.currentTime || 0) * 1000)
    track('play_ended', {
      day: feedbackVideoTrackInfo.day,
      type: feedbackVideoTrackInfo.type,
      learner_id: feedbackVideoTrackInfo.learnerId,
      playback_id: feedbackVideoTrackInfo.playbackId,
      position_ms: positionMs,
      total_ms: feedbackVideoTrackInfo.totalMs,
      session_id: getSessionId()
    })
  }
  feedbackVideoTrackInfo = null
}

function onFeedbackVideoError() {
  feedbackPlaying.value = false
  feedbackVideoVisible.value = false
  feedbackVideoTrackInfo = null
  feedbackMessage.value = '再生に失敗しました'
  resetAssignmentImage()
}

// 直近の先生コメントを取得して再生（UIなし）＋ 視聴データ送信
const playLatestFeedback = async () => {
  if (feedbackLoading.value || feedbackPlaying.value) return
  feedbackMessage.value = null
  feedbackLoading.value = true

  // タップ直後に同じ Audio で無音再生して iOS の再生制限を解除（await しない）
  const audio = new Audio(SILENT_WAV_DATA_URL)
  audio.volume = 0
  audio.play().catch(() => {})

  // 動画になる場合に備えて、同じタップ操作のうちに <video> 要素も一度再生を試みて解除しておく
  // （await を挟んだ後に初めて play() すると、iOSではユーザー操作と紐付かず再生がブロックされるため）
  const videoElForUnlock = feedbackVideoEl.value
  if (videoElForUnlock) {
    videoElForUnlock.play().catch(() => {})
    videoElForUnlock.pause()
  }

  try {
    const { data: { session } } = await supabase.auth.getSession()
    const token = session?.access_token
    if (!token) {
      feedbackMessage.value = 'ログインが必要です'
      return
    }

    let query = supabase
      .from('submissions_v3')
      .select('id, day, type')
      .eq('user_id', currentUserId)
      .not('feedback_file_key', 'is', null)
      .order('feedback_submitted_at', { ascending: false, nullsFirst: false })
      .limit(1)
    if (selectedLearnerId.value) {
      query = query.eq('learner_id', selectedLearnerId.value)
    }
    const { data: latestList, error: listError } = await query

    if (listError) {
      console.error('直近FB取得エラー:', listError)
      feedbackMessage.value = 'コメントの取得に失敗しました'
      return
    }
    if (!latestList?.length) {
      feedbackMessage.value = 'まだ先生からのコメントはありません'
      return
    }

    const row = latestList[0]
    const submissionId = row.id
    latestFeedbackDay.value = row.day
    latestFeedbackType.value = row.type
    const base = getApiBase()
    const url = `${base ? base + '/' : ''}api/feedback-by-submission?submissionId=${submissionId}`
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } })
    const result = await res.json().catch(() => ({}))

    if (!res.ok || !result.exists || !result.signedUrl) {
      feedbackMessage.value = result.message || 'コメントを取得できませんでした'
      return
    }

    const day = result.day ?? row.day ?? 1
    const type = result.type ?? row.type ?? 'practice'
    latestFeedbackDay.value = day
    latestFeedbackType.value = type
    const totalMs = (result.totalDuration || 0) * 1000
    const playbackId = generatePlaybackId()

    track('play_started', {
      day,
      type,
      learner_id: selectedLearnerId.value,
      playback_id: playbackId,
      total_ms: totalMs,
      session_id: getSessionId()
    })

    // 動画フィードバック（feedback_mime_type が video/ で始まる場合）
    if (result.mimeType && result.mimeType.startsWith('video/')) {
      feedbackVideoTrackInfo = { day, type, learnerId: selectedLearnerId.value, playbackId, totalMs }
      feedbackVideoVisible.value = true
      feedbackPlaying.value = true
      assignmentImageLoadFailed.value = false
      assignmentImageUrl.value = assignmentImageUrlForDay(day)
      await nextTick()
      const videoEl = feedbackVideoEl.value
      if (videoEl) {
        videoEl.src = result.signedUrl
        try {
          await videoEl.play()
        } catch (playError) {
          console.warn('先生コメント動画再生エラー:', playError)
          feedbackMessage.value = '再生に失敗しました'
          feedbackPlaying.value = false
          feedbackVideoVisible.value = false
        }
      }
      return
    }

    audio.volume = 1
    audio.src = result.signedUrl
    audio.onended = () => {
      feedbackPlaying.value = false
      const positionMs = Math.round((audio.currentTime || 0) * 1000)
      track('play_ended', {
        day,
        type,
        learner_id: selectedLearnerId.value,
        playback_id: playbackId,
        position_ms: positionMs,
        total_ms: totalMs,
        session_id: getSessionId()
      })
    }
    audio.onerror = () => {
      feedbackPlaying.value = false
      console.warn('先生コメント再生エラー:', audio.error)
      feedbackMessage.value = '再生に失敗しました'
    }
    feedbackPlaying.value = true
    await audio.play()
  } catch (err) {
    feedbackPlaying.value = false
    console.error('先生のコメント再生エラー:', err)
    feedbackMessage.value = 'コメントの取得または再生に失敗しました'
  } finally {
    feedbackLoading.value = false
  }
}

// Practice に表示中の Day を渡す（動画・提出先と同期）
const goToPractice = () => {
  // ボタンの無効化だけに頼らない（必須データ未確定・読み込み中は始めない。指示書 A-23）
  if (!canStartPractice({ loading: loading.value, requiredDataReady: requiredDataReady.value })) return
  router.push({ path: '/practice', query: { day: currentDay.value } })
}

const todayYmdJst = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Asia/Tokyo' })

const shouldResyncOnResume = () => {
  const now = Date.now()
  const ymd = todayYmdJst()
  if (!lastResyncAt.value) return true
  if (ymd !== lastResyncYmd.value) return true
  return (now - lastResyncAt.value) >= RESYNC_INTERVAL_MS
}

const markResynced = () => {
  lastResyncAt.value = Date.now()
  lastResyncYmd.value = todayYmdJst()
}

const resyncOnResume = async () => {
  if (isResyncing || !shouldResyncOnResume()) return
  isResyncing = true
  try {
    await loadProgress()
    markResynced()
  } catch (e) {
    console.warn('Top resume resync error:', e)
  } finally {
    isResyncing = false
  }
}

const handleVisibilityChange = () => {
  if (document.visibilityState === 'visible') {
    resyncOnResume()
  }
}

const handleWindowFocus = () => {
  resyncOnResume()
}

onMounted(() => {
  loadProgress()
  markResynced()
  document.addEventListener('visibilitychange', handleVisibilityChange)
  window.addEventListener('focus', handleWindowFocus)
})

onUnmounted(() => {
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  window.removeEventListener('focus', handleWindowFocus)
})
</script>

<style scoped>
.required-data-error {
  width: 100%;
  max-width: 400px;
  margin: 0 auto 1rem;
  padding: 0.75rem 1rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #991b1b;
  text-align: center;
}
.required-data-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  margin: -0.5rem 0 -0.75rem;
  color: #475569;
  font-size: 0.95rem;
}

.required-data-spinner {
  width: 1.1rem;
  height: 1.1rem;
  border: 2px solid #cbd5e1;
  border-top-color: #667eea;
  border-radius: 50%;
  animation: required-data-spin 0.8s linear infinite;
  flex-shrink: 0;
}

@keyframes required-data-spin {
  to { transform: rotate(360deg); }
}

@media (prefers-reduced-motion: reduce) {
  .required-data-spinner {
    animation-duration: 2.4s;
  }
}

.required-data-switch {
  width: 100%;
  max-width: 400px;
  margin: 0 auto 1rem;
  padding: 0.75rem 1rem;
  background: #fffbeb;
  border: 1px solid #fcd34d;
  border-radius: 8px;
  color: #92400e;
}

.required-data-switch p {
  margin: 0 0 0.75rem;
  line-height: 1.6;
}

.required-data-switch-buttons {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.required-data-error p {
  margin: 0 0 0.5rem;
  line-height: 1.6;
}

.top-page {
  min-height: 60vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem 1rem;
  position: relative;
}

.top-header {
  width: 100%;
  max-width: 400px;
  margin-bottom: 3rem;
}

.day-label {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
  text-align: center;
}

.top-main {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 420px;
}

.buttons {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 100%;
}

.feedback-btn-wrap {
  position: relative;
  width: 100%;
}

.feedback-btn-wrap--badged {
  margin-top: 0.35rem;
}

.feedback-day-badge {
  position: absolute;
  top: -0.45rem;
  left: 0.65rem;
  z-index: 2;
  padding: 0.2rem 0.55rem;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: #4338ca;
  background: #eef2ff;
  border: 1px solid #c7d2fe;
  border-radius: 6px;
  line-height: 1.2;
  pointer-events: none;
  box-shadow: 0 1px 2px rgba(67, 56, 202, 0.12);
}

.feedback-message {
  margin: 0;
  padding: 0.5rem 0;
  font-size: 0.9rem;
  color: #64748b;
  text-align: center;
}

.feedback-video-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  flex-direction: column;
  align-items: center;
  /* 動画＋画像＋閉じるボタンの合計が画面高を超える端末があるため、
     centerではなくflex-startにして、はみ出した分を確実にスクロールで閉じるボタンまで到達できるようにする
     （centerだとSafariのバージョンによってはみ出し分が上に隠れて閉じるボタンに届かなくなるリスクがあるため） */
  justify-content: flex-start;
  gap: 1rem;
  z-index: 1000;
  padding: 1rem;
  overflow-y: auto;
}

.feedback-video-player {
  width: 100%;
  max-width: 360px;
  max-height: 42vh;
  aspect-ratio: 3 / 4;
  object-fit: contain;
  background: #000;
  border-radius: 8px;
  flex-shrink: 0;
}

/* 動画（縦長・幅360px上限）とは別に、下段はこの日の課題内容を横向きで画面幅いっぱいに近く表示する */
.feedback-assignment-image {
  width: min(480px, 92vw);
  max-height: 28vh;
  aspect-ratio: 852 / 480;
  object-fit: cover;
  background: #000;
  border-radius: 8px;
  flex-shrink: 0;
}

.feedback-video-close {
  padding: 0.5rem 1.25rem;
  border-radius: 9999px;
  border: none;
  background: #f1f5f9;
  color: #1f2937;
  font-size: 0.95rem;
  cursor: pointer;
}

.btn {
  width: 100%;
  padding: 1.25rem 1.5rem;
  font-size: 1.125rem;
  font-weight: 600;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s;
}

.btn:active {
  transform: scale(0.98);
}

.btn-primary {
  background: #667eea;
  color: white;
  box-shadow: 0 4px 14px rgba(102, 126, 234, 0.4);
}

.btn-primary:hover {
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.5);
}

/* 先生のコメント再生中は色を変えて、押せないようにする（連打防止） */
.top-page .btn-primary.btn-primary--playing:disabled {
  background: #059669;
  color: #ffffff;
  box-shadow: 0 0 0 4px rgba(5, 150, 105, 0.2);
  cursor: default;
  opacity: 1;
}

.playing-content {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.icon-playing {
  display: inline-block;
  animation: pulse-sound 0.8s infinite alternate ease-in-out;
}

@keyframes pulse-sound {
  0% { transform: scale(0.9); opacity: 0.8; }
  100% { transform: scale(1.2); opacity: 1; }
}

.btn-secondary {
  background: #f8fafc;
  color: #334155;
  border: 2px solid #e2e8f0;
}

.btn-secondary:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.btn-mic-check {
  background: #fff7ed;
  color: #9a3412;
  border: 2px solid #fdba74;
  font-size: 1rem;
  padding: 0.9rem 1.5rem;
}

.btn-mic-check:hover {
  background: #ffedd5;
  border-color: #fb923c;
}

.mic-check-hint {
  margin: -0.75rem 0 0;
  font-size: 0.8rem;
  color: #6b7280;
  line-height: 1.5;
  text-align: center;
}

/* これまでのレッスン動画：ボタンより控えめな文字のリンク（今日の練習が主役） */
.past-videos-link {
  align-self: center;
  padding: 0.5rem 0.75rem;
  font-size: 0.9rem;
  color: #4f46e5;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.past-videos-link:hover {
  color: #4338ca;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  z-index: 1000;
}

.modal-box {
  background: #fff;
  border-radius: 16px;
  padding: 2rem 1.5rem;
  max-width: 420px;
  width: 100%;
  text-align: center;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  max-height: 85vh;
  overflow-y: auto;
}

.modal-box--wide {
  max-width: 480px;
  text-align: left;
}

.modal-emoji {
  font-size: 2.5rem;
  margin: 0 0 0.5rem;
}

.modal-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 0.75rem;
}

.modal-body {
  font-size: 1rem;
  color: #374151;
  line-height: 1.6;
  margin: 0 0 0.75rem;
}

.modal-body--sub {
  font-size: 0.85rem;
  color: #6b7280;
  padding-top: 0.5rem;
  border-top: 1px solid #e5e7eb;
}

.modal-box .btn {
  margin-top: 0.5rem;
}

.modal-close-btn {
  margin-top: 1rem;
}

</style>
